import { BorderedContainer } from 'core/components/common/BorderedContainer';
import { IncidentDetailPatientForSendai } from 'sections/sendai/features/useIncidentDetailForSendai';
import { SectionTitle } from 'core/components/common/SectionTitle';
import { AccordionToggle } from 'core/components/JianDetail/PatientDetailInfo/common/AccordionToggle';
import { useOpenClose } from 'core/hooks/useOpenClose';
import { AccordionToggleContent } from 'core/components/JianDetail/PatientDetailInfo/common/AccordionToggleContent';
import { InfoDisplay } from 'core/components/JianDetail/common/InfoDisplay';
import { convertToPresenceAbsence } from 'core/components/common/converters';
import { formatDateTime } from 'core/utils/date';
import { DataNotFound } from 'core/components/common/DataNotFound';
import { FC } from 'react';

type Props = {
  /** バイタルリスト。 */
  vitals: IncidentDetailPatientForSendai['vitals'];
  /** 搬送体位。 */
  transportBodyPosition?: string;
};

export const VitalsView: FC<Props> = ({ vitals, transportBodyPosition }) => {
  return (
    <BorderedContainer>
      <div className="flex flex-col gap-[24px]">
        <SectionTitle text="バイタル" />
        <div className="flex flex-col gap-[8px]">
          {vitals.length > 0 ? (
            vitals.map(vital => (
              <VitalDetailView
                key={vital.vitalId}
                vitalData={vital.data}
                transportBodyPosition={transportBodyPosition}
              />
            ))
          ) : (
            <DataNotFound />
          )}
        </div>
      </div>
    </BorderedContainer>
  );
};

type VitalDetailViewProps = {
  /** バイタル。 */
  vitalData: IncidentDetailPatientForSendai['vitals'][number]['data'];
  /** 搬送体位。 */
  transportBodyPosition?: string;
};

const VitalDetailView = ({
  vitalData,
  transportBodyPosition,
}: VitalDetailViewProps) => {
  const { isOpen, handleOpen, handleClose } = useOpenClose();

  if (!vitalData) {
    return null;
  }

  const formatWithUnit = (
    value: number | string | null | undefined,
    unit = ''
  ) =>
    value !== null && value !== undefined && value !== ''
      ? `${value}${unit}`
      : '';

  const recordedAtText = vitalData.recordedAt
    ? formatDateTime(vitalData.recordedAt, 'yyyy/MM/dd HH:mm')
    : '';

  return (
    <>
      <AccordionToggle
        isOpen={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
      >
        <p className="text-[16px] noto-sans-jp-medium text-textPrimary">
          {recordedAtText}
          {vitalData.measureTiming ? `（${vitalData.measureTiming}）` : ''}
        </p>
      </AccordionToggle>
      <AccordionToggleContent isOpen={isOpen}>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay title="記録時間" content={recordedAtText} />
          <InfoDisplay
            title="観察時期"
            content={vitalData.measureTiming ?? ''}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay title="JCS" content={vitalData.jcs ?? ''} />
          <InfoDisplay
            title="R: 不穏"
            content={convertToPresenceAbsence(vitalData?.riaR)}
          />
          <InfoDisplay
            title="I: 糞尿失禁"
            content={convertToPresenceAbsence(vitalData?.riaI)}
          />
          <InfoDisplay
            title="A: 自発性喪失"
            content={convertToPresenceAbsence(vitalData?.riaA)}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay title="E(GCS)" content={vitalData.gcsE ?? ''} />
          <InfoDisplay title="V(GCS)" content={vitalData.gcsV ?? ''} />
          <InfoDisplay title="M(GCS)" content={vitalData.gcsM ?? ''} />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="脈拍数"
            content={formatWithUnit(vitalData?.pr, ' 回/min')}
          />
          <InfoDisplay
            title="呼吸数"
            content={vitalData.rr ? `${vitalData.rr} 回/min` : ''}
          />
          <InfoDisplay
            title="体温"
            content={vitalData.bt ? `${vitalData.bt} °C` : ''}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="瞳孔(左)"
            content={
              vitalData.pupilSizeLeft ? `${vitalData.pupilSizeLeft} mm` : ''
            }
          />
          <InfoDisplay
            title="対光反射(左)"
            content={vitalData.pupilReactionLeft ?? ''}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="瞳孔(右)"
            content={
              vitalData.pupilSizeRight ? `${vitalData.pupilSizeRight} mm` : ''
            }
          />
          <InfoDisplay
            title="対光反射(右)"
            content={vitalData.pupilReactionRight ?? ''}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="BP/収縮(左)"
            content={formatWithUnit(vitalData?.sbpLeft, ' mmHg')}
          />
          <InfoDisplay
            title="BP/拡張(左)"
            content={formatWithUnit(vitalData?.dbpLeft, ' mmHg')}
          />
          <InfoDisplay
            title="BP/収縮(右)"
            content={formatWithUnit(vitalData?.sbpRight, ' mmHg')}
          />
          <InfoDisplay
            title="BP/拡張(右)"
            content={formatWithUnit(vitalData?.dbpRight, ' mmHg')}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay title="ECG" content={vitalData.ecgWaveform ?? ''} />
          <InfoDisplay
            title="SpO2"
            content={
              vitalData.spo2 || vitalData.spo2After
                ? `${vitalData.spo2 ?? '-'} % → ${vitalData.spo2After ?? '-'} %`
                : ''
            }
          />
          <InfoDisplay title="投与方法" content={vitalData.o2Means ?? ''} />
          <InfoDisplay
            title="酸素投与"
            content={vitalData.o2Flow ? `${vitalData.o2Flow} L/min` : ''}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay title="搬送体位" content={transportBodyPosition ?? ''} />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay title="処置判断等" content={vitalData.vitalNote ?? ''} />
        </div>
      </AccordionToggleContent>
    </>
  );
};
